/**
 * Header 用の共通アトム。
 * dental-avatar_files/header-atoms.jsx（B案）をTypeScript移植。
 */
import type { CSSProperties, ReactNode } from "react";
import type { StaffRole } from "../types";

export const HDR_COLORS = {
  bg: "#FFFFFF",
  line: "#E4E4E7",
  lineSoft: "#F1F1EF",
  ink: "#18181B",
  inkMid: "#52525B",
  inkMute: "#71717A",
  inkFade: "#A1A1AA",
  roleAdmin: { bg: "#18181B", fg: "#FFFFFF", border: "#18181B" },
  roleEducator: { bg: "#ECF5EE", fg: "#2F6A3A", border: "#CFE4D4" },
  roleStudent: { bg: "#EEF2F7", fg: "#3F4C6B", border: "#DBE1EA" },
  hover: "#F4F4F5",
  focus: "#18181B",
} as const;

export const HDR_FONT = '"Hiragino Sans", "Noto Sans JP", system-ui, sans-serif';

interface HeaderLogoMarkProps {
  size?: number;
  dark?: boolean;
}

export function HeaderLogoMark({ size = 28, dark }: HeaderLogoMarkProps) {
  const fg = dark ? "#FFFFFF" : "#18181B";
  const bg = dark ? "#18181B" : "#FFFFFF";
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="32" height="32" rx="7" fill={bg} />
      {dark && <rect width="32" height="32" rx="7" fill="#18181B" />}
      <path
        d="M11 9.2c0-1.6 1.3-2.8 2.9-2.8 1.2 0 1.8.6 2.1.6.3 0 1-.6 2.1-.6 1.6 0 2.9 1.2 2.9 2.8 0 2.5-1 4-1.8 6.5-.2.7-.3 2-.6 3.2-.2.9-.8 1.5-1.5 1.5-.9 0-1.2-1.4-1.5-2.6-.2-.6-.4-1-.6-1-.2 0-.4.4-.6 1-.3 1.2-.6 2.6-1.5 2.6-.7 0-1.3-.6-1.5-1.5-.3-1.2-.4-2.5-.6-3.2-.8-2.5-1.8-4-1.8-6.5z"
        fill={fg}
      />
      <circle cx="23.5" cy="23" r="2.2" fill={fg} />
    </svg>
  );
}

type WordmarkVariant =
  | "mark-only"
  | "en-primary"
  | "en-compact"
  | "ja-refined"
  | "monogram";

interface HeaderWordmarkProps {
  variant?: WordmarkVariant;
  dark?: boolean;
}

export function HeaderWordmark({ variant = "en-primary", dark }: HeaderWordmarkProps) {
  const fg = dark ? "#FFFFFF" : HDR_COLORS.ink;
  const sub = dark ? "rgba(255,255,255,0.55)" : HDR_COLORS.inkMute;

  if (variant === "mark-only") return null;

  if (variant === "monogram") {
    return (
      <span
        style={{
          fontSize: 16,
          fontWeight: 800,
          letterSpacing: 1,
          color: fg,
          fontFamily: HDR_FONT,
        }}
      >
        DA
      </span>
    );
  }

  if (variant === "en-primary") {
    return (
      <div style={{ lineHeight: 1.1, fontFamily: HDR_FONT, whiteSpace: "nowrap" }}>
        <div
          style={{
            fontSize: 15,
            fontWeight: 800,
            color: fg,
            letterSpacing: -0.3,
            whiteSpace: "nowrap",
          }}
        >
          Dental Avatar
        </div>
        <div
          style={{
            fontSize: 10,
            color: sub,
            fontWeight: 600,
            letterSpacing: 0.8,
            marginTop: 2,
            whiteSpace: "nowrap",
          }}
        >
          歯科医療面接トレーナー
        </div>
      </div>
    );
  }

  if (variant === "en-compact") {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 8,
          fontFamily: HDR_FONT,
          whiteSpace: "nowrap",
        }}
      >
        <span
          style={{
            fontSize: 15,
            fontWeight: 800,
            color: fg,
            letterSpacing: -0.3,
            whiteSpace: "nowrap",
          }}
        >
          Dental Avatar
        </span>
        <span
          style={{
            fontSize: 10,
            color: sub,
            fontWeight: 600,
            letterSpacing: 0.8,
            paddingLeft: 8,
            borderLeft: `1px solid ${dark ? "rgba(255,255,255,0.2)" : HDR_COLORS.line}`,
            whiteSpace: "nowrap",
          }}
        >
          歯科医療面接トレーナー
        </span>
      </div>
    );
  }

  if (variant === "ja-refined") {
    return (
      <div style={{ lineHeight: 1.1, fontFamily: HDR_FONT, whiteSpace: "nowrap" }}>
        <div
          style={{
            fontSize: 15,
            fontWeight: 800,
            color: fg,
            letterSpacing: 0.02,
            whiteSpace: "nowrap",
          }}
        >
          デンタルアバター
        </div>
        <div
          style={{
            fontSize: 9,
            color: sub,
            fontWeight: 700,
            letterSpacing: 2,
            marginTop: 3,
            whiteSpace: "nowrap",
          }}
        >
          DENTAL AVATAR TRAINER
        </div>
      </div>
    );
  }

  return null;
}

interface RoleBadgeProps {
  role?: StaffRole | null;
  size?: "sm" | "md";
}

export function RoleBadge({ role, size = "sm" }: RoleBadgeProps) {
  if (!role) return null;
  const map: Record<StaffRole, { bg: string; fg: string; border: string; label: string }> = {
    admin: { ...HDR_COLORS.roleAdmin, label: "管理者" },
    educator: { ...HDR_COLORS.roleEducator, label: "教員" },
    student: { ...HDR_COLORS.roleStudent, label: "学生" },
  };
  const s = map[role];
  const sz: CSSProperties =
    size === "sm"
      ? { padding: "2px 8px", fontSize: 10, letterSpacing: 0.4 }
      : { padding: "3px 10px", fontSize: 11, letterSpacing: 0.4 };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        background: s.bg,
        color: s.fg,
        border: `1px solid ${s.border}`,
        borderRadius: 4,
        fontWeight: 700,
        lineHeight: 1.4,
        fontFamily: HDR_FONT,
        whiteSpace: "nowrap",
        ...sz,
      }}
    >
      {s.label}
    </span>
  );
}

interface NavButtonProps {
  icon?: ReactNode;
  label?: string;
  variant?: "ghost" | "outline" | "icon";
  onClick?: () => void;
  badge?: string | number;
  ariaLabel?: string;
}

export function NavButton({
  icon,
  label,
  variant = "outline",
  onClick,
  badge,
  ariaLabel,
}: NavButtonProps) {
  const base: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    flexShrink: 0,
    padding: variant === "icon" ? "0" : "7px 11px",
    width: variant === "icon" ? 30 : undefined,
    height: variant === "icon" ? 30 : 30,
    borderRadius: 7,
    fontSize: 12,
    fontWeight: 600,
    fontFamily: HDR_FONT,
    letterSpacing: 0.02,
    cursor: "pointer",
    background: variant === "outline" ? "#fff" : "transparent",
    color: HDR_COLORS.inkMid,
    border: variant === "outline" ? `1px solid ${HDR_COLORS.line}` : "1px solid transparent",
    transition: "background 120ms ease, color 120ms ease, border-color 120ms ease",
    position: "relative",
    justifyContent: variant === "icon" ? "center" : undefined,
    whiteSpace: "nowrap",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel ?? label}
      title={ariaLabel ?? label}
      style={base}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = HDR_COLORS.hover;
        e.currentTarget.style.color = HDR_COLORS.ink;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background =
          variant === "outline" ? "#fff" : "transparent";
        e.currentTarget.style.color = HDR_COLORS.inkMid;
      }}
    >
      {icon}
      {variant !== "icon" && label && <span style={{ whiteSpace: "nowrap" }}>{label}</span>}
      {badge !== undefined && badge !== null && badge !== "" && (
        <span
          style={{
            position: "absolute",
            top: -2,
            right: -2,
            minWidth: 14,
            height: 14,
            padding: "0 4px",
            borderRadius: 7,
            background: "#D9483B",
            color: "#fff",
            fontSize: 9,
            fontWeight: 800,
            lineHeight: "14px",
            textAlign: "center",
            border: "1.5px solid #fff",
          }}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

// --- icons ---
interface IconProps {
  size?: number;
}

export function IconAward({ size = 13 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="8" r="6" />
      <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" />
    </svg>
  );
}

export function IconLogout({ size = 13 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

export function IconTools({ size = 13 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  );
}

export function IconHome({ size = 13 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 10.5L12 3l9 7.5" />
      <path d="M5 9.8V20a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V9.8" />
    </svg>
  );
}
